import { useState, useEffect, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Search, ExternalLink, Filter, X } from "lucide-react";
import normativaData from "@/data/normativa.json";
import entregablesData from "@/data/entregables.json";

const ambitoLabels = {
  UE: { label: "Unión Europea", color: "bg-blue-100 text-blue-700" },
  ES: { label: "España", color: "bg-red-100 text-red-700" },
  IB: { label: "Illes Balears", color: "bg-amber-100 text-amber-700" },
};

export default function Normativa() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedWp, setSelectedWp] = useState(searchParams.get("wp") || "");

  useEffect(() => {
    const wpParam = searchParams.get("wp");
    if (wpParam) {
      setSelectedWp(wpParam);
    }
  }, [searchParams]);

  const filteredNormativa = useMemo(() => {
    return normativaData.filter((norm) => {
      const matchesSearch =
        searchQuery === "" ||
        norm.titulo.toLowerCase().includes(searchQuery.toLowerCase()) ||
        norm.descripcion.toLowerCase().includes(searchQuery.toLowerCase()) ||
        norm.tags.some((tag) =>
          tag.toLowerCase().includes(searchQuery.toLowerCase())
        );

      const matchesWp =
        selectedWp === "" || norm.wps.includes(selectedWp);

      return matchesSearch && matchesWp;
    });
  }, [searchQuery, selectedWp]);

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedWp("");
    setSearchParams({});
  };

  const handleWpFilter = (wpId) => {
    if (wpId === selectedWp) {
      setSelectedWp("");
      setSearchParams({});
    } else {
      setSelectedWp(wpId);
      setSearchParams({ wp: wpId });
    }
  };

  return (
    <div className="min-h-screen pt-20" data-testid="normativa-page">
      <Helmet>
        <title>Normativa Aplicable | SOSTRE BALEAR</title>
        <meta name="description" content="Marco legal y normativo para la vivienda industrializada en las Illes Balears: Ley 3/2024, Decreto-Ley 3/2025, LUIB, Ley de Vivienda estatal y más." />
        <link rel="canonical" href="https://www.sostrebalear.com/normativa" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Normativa Aplicable | SOSTRE BALEAR" />
        <meta property="og:description" content="Marco legal para vivienda industrializada en Baleares." />
        <meta property="og:url" content="https://www.sostrebalear.com/normativa" />
        <meta property="og:image" content="https://www.sostrebalear.com/assets/logos/sostre-balear.png" />
        <meta property="og:locale" content="es_ES" />
        <meta property="og:site_name" content="SOSTRE BALEAR" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Normativa Aplicable | SOSTRE BALEAR" />
        <meta name="twitter:description" content="Marco legal para vivienda industrializada en Baleares." />
      </Helmet>
      {/* Header */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-sm font-medium tracking-wide uppercase text-[#22c55e]">
              Marco Legal
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mt-3 tracking-tight">
              Normativa Aplicable
            </h1>
            <p className="text-lg text-slate-600 mt-6 leading-relaxed">
              Recopilación de la normativa europea, estatal y autonómica
              relevante para el modelo SOSTRE BALEAR.
            </p>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-white border-b border-slate-200 sticky top-16 md:top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search
                className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                size={20}
              />
              <input
                type="text"
                placeholder="Buscar normativa..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-full border border-slate-200 focus:border-[#22c55e] focus:ring-2 focus:ring-[#22c55e]/20 outline-none transition-all"
                data-testid="normativa-search"
              />
            </div>

            {/* WP Filter */}
            <div className="flex items-center gap-2 flex-wrap">
              <Filter size={18} className="text-slate-400" />
              <span className="text-sm text-slate-500 mr-2">Filtrar por WP:</span>
              {entregablesData.map((wp) => (
                <button
                  key={wp.id}
                  onClick={() => handleWpFilter(wp.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    selectedWp === wp.id
                      ? "bg-[#22c55e] text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                  data-testid={`filter-${wp.id}`}
                >
                  WP{wp.numero}
                </button>
              ))}
            </div>

            {/* Clear Filters */}
            {(searchQuery || selectedWp) && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 transition-colors"
                data-testid="clear-filters"
              >
                <X size={16} />
                Limpiar filtros
              </button>
            )}
          </div>

          {/* Active Filter Display */}
          {selectedWp && (
            <div className="mt-4">
              <span className="text-sm text-slate-600">
                Mostrando normativa relacionada con:{" "}
                <strong className="text-[#22c55e]">
                  {entregablesData.find((wp) => wp.id === selectedWp)?.titulo}
                </strong>
              </span>
            </div>
          )}
        </div>
      </section>

      {/* Results */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredNormativa.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-slate-500 text-lg">
                No se encontró normativa con los filtros aplicados.
              </p>
              <button
                onClick={clearFilters}
                className="mt-4 text-[#22c55e] hover:underline"
              >
                Limpiar filtros
              </button>
            </div>
          ) : (
            <div className="grid gap-6" data-testid="normativa-list">
              {filteredNormativa.map((norm) => (
                <article
                  key={norm.id}
                  className="bg-white rounded-2xl border border-slate-100 p-6 hover:shadow-md transition-shadow"
                  data-testid={`normativa-card-${norm.id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            ambitoLabels[norm.ambito].color
                          }`}
                        >
                          {ambitoLabels[norm.ambito].label}
                        </span>
                        <div className="flex gap-1">
                          {norm.wps.map((wpId) => (
                            <Link
                              key={wpId}
                              to={`/entregables/${wpId}`}
                              className="px-2 py-0.5 bg-slate-100 hover:bg-[#22c55e]/10 text-slate-600 hover:text-[#22c55e] rounded text-xs font-medium transition-colors"
                            >
                              WP
                              {
                                entregablesData.find((wp) => wp.id === wpId)
                                  ?.numero
                              }
                            </Link>
                          ))}
                        </div>
                      </div>

                      <h3 className="text-lg font-semibold text-slate-900 mb-2">
                        {norm.titulo}
                      </h3>
                      <p className="text-slate-600 text-sm">
                        {norm.descripcion}
                      </p>
                    </div>

                    <a
                      href={norm.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#22c55e] hover:bg-[#16a34a] text-white px-6 py-3 rounded-full font-medium text-sm transition-all shrink-0"
                      data-testid={`link-${norm.id}`}
                    >
                      Abrir enlace
                      <ExternalLink size={16} />
                    </a>
                  </div>
                </article>
              ))}
            </div>
          )}

          <p className="text-center text-slate-500 text-sm mt-8">
            Mostrando {filteredNormativa.length} de {normativaData.length}{" "}
            normativas
          </p>
        </div>
      </section>

      {/* Info Note */}
      <section className="py-12 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-600 text-sm">
            Esta sección contiene enlaces a normativa externa. Los documentos
            originales se encuentran en las webs oficiales de las instituciones
            correspondientes.
          </p>
        </div>
      </section>
    </div>
  );
}
